<template>
  <div id="dossierItemWrapper">
    <div class="card" :class="activeSource === 3 ? 'kennisenvaardigheden' : 'item'">
      <div class="card-header" @click="toggle">
        <p class="card-header-title">
          {{ item.hoofdstuk }} {{ item.titel }}
          <span v-show="hasItemsWithoutQuestions()" class="tag is-danger is-rounded counter"></span>
        </p>

        <p class="card-header-icon" aria-label="more options">
          <span class="icon">
            <i class="fas" :class="isExpanded ? 'fa-angle-down' : 'fa-angle-left'" aria-hidden="true"></i>
          </span>
        </p>
      </div>

      <div v-show="isExpanded" class="card-content">
        <!-- kerntaak -->
        <template v-if="activeSource === 3">
          <div class="card">
            <div class="card-header">
              <p class="card-header-title">Algemeen</p>
            </div>

            <!-- basis || profiel: [itemNr, kvNr, profileNr] -->
            <div
              v-for="kv in item.refs.algemeen"
              :key="kv.nummer"
              class="card-content">

              <div class="content" v-html="kv.titel"></div>

              <questions
                v-if="inAdmin || hasBundles"
                :itemNr="item.nr"
                :kvNr="kv.nr"
                :profileNr="item.profiel"
                :activeSource="activeSource"
                :text="kv.titel"
                :isOpened="isOpened"
                @count="counter"
              ></questions>
            </div>
          </div>

          <!-- categorie: [itemNr, kvNr, categoryNr, profileNr?] -->
          <div
            v-for="categorie in item.refs['per-categorie']"
            :key="categorie.categorie"
            class="card">

            <div class="card-header">
              <p class="card-header-title">{{ getCategory(categorie.categorie).titel }}</p>
            </div>

            <div
              v-for="kv in categorie.refs"
              :key="kv.nr">

              <div
                v-if="kv"
                class="card-content">

                <div class="content" v-html="kv.titel"></div>

                <questions
                  v-if="inAdmin || hasBundles"
                  :itemNr="item.nr"
                  :kvNr="kv.nr"
                  :activeSource="activeSource"
                  :profileNr="item.profiel"
                  :categoryNr="categorie.categorie"
                  :text="kv.titel"
                  :isOpened="isOpened"
                  @count="counter"
                ></questions>
              </div>

              <div v-else class="card-content">
                <div class="notification is-warning">
                  Bron niet gevonden
                </div>
              </div>
            </div>
          </div>

          <!-- aanvullend: [itemNr, kvNr, profileNr] -->
          <template v-if="hasAanvullend">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">Aanvullend</p>

                <div class="tabs is-toggle is-small">
                  <ul>
                    <li
                      v-for="(profile, index) in profiles"
                      :key="profile.nr"
                      :class="{ 'is-active': activeTab === index }"
                      @click="toggleProfile(index)">

                      <a><span>{{ profile.titel }}</span></a>
                    </li>
                  </ul>
                </div>
              </div>

              <div
                v-for="kv in item.refs['per-profiel'][activeTab].refs"
                :key="kv.nr"
                class="card-content">

                <div class="content" v-html="kv.titel"></div>

                <questions
                  v-if="inAdmin || hasBundles && profileIsSet(profileNr)"
                  :itemNr="item.nr"
                  :kvNr="kv.nr"
                  :activeSource="activeSource"
                  :profileNr="profileNr"
                  :text="kv.titel"
                  :isOpened="isOpened"
                  @count="counter"
                ></questions>
              </div>
            </div>
          </template>
        </template>
        <!-- /kerntaak> -->

        <!-- werkproces -->
        <template v-else>
          <h2 class="title is-6">{{ item.profiel ? getProfile(item.profiel).titel : "Algemeen" }}</h2>

          <!-- basis || profiel: [itemNr, profileNr] -->
          <div
            class="content"
            v-html="activeSource === 1 ? item.omschrijving.algemeen : item.gedrag.algemeen">
          </div>

          <questions
            v-if="inAdmin || hasBundles"
            :itemNr="item.nr"
            :profileNr="item.profiel"
            :activeSource="activeSource"
            :text="activeSource === 1 ? item.omschrijving.algemeen : item.gedrag.algemeen"
            :isOpened="isOpened"
            @count="counter">
          </questions>

          <!-- aanvullend: [itemNr, profileNr] -->
          <template v-if="hasAanvullend">
            <hr>

            <h2 class="title is-6">Aanvullend</h2>

            <div class="tabs is-boxed is-fullwidth is-small">
              <ul>
                <li
                  v-for="(profile, index) in profiles"
                  :key="profile.nr"
                  :class="{ 'is-active': activeTab === index }"
                  @click="toggleProfile(index)">

                  <a>
                    <span>{{ profile.titel }}</span>
                  </a>
                </li>
              </ul>
            </div>

            <div
              class="content"
              v-html="activeSource === 1 ? item.omschrijving.aanvullend[activeTab].omschrijving : item.gedrag.aanvullend[activeTab].omschrijving">
            </div>

            <questions
              v-if="inAdmin || hasBundles && profileIsSet(profileNr)"
              :itemNr="item.nr"
              :profileNr="profileNr"
              :activeSource="activeSource"
              :text="activeSource === 1 ? item.omschrijving.aanvullend[activeTab].omschrijving : item.gedrag.aanvullend[activeTab].omschrijving"
              :isOpened="isOpened"
              @count="counter"
            ></questions>

          </template>
        </template>
        <!-- /werkproces -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { authMixin } from '@/mixins'
import Questions from '@/components/questions/Questions'

export default {
  props: [
    'item',
    'activeSource',
    'isOpened'
  ],
  mixins: [
    authMixin
  ],
  components: {
    Questions
  },
  data () {
    return {
      activeTab: 0,
      isExpanded: false,
      showQuestionsGeneral: false,
      showQuestionsAdditional: false,
      counts: []
    }
  },
  computed: {
    ...mapGetters({
      account: 'account/account',
      source: 'dossiers/source',
      dossier: 'dossiers/dossier'
    }),
    inAdmin () {
      return this.$route.name === 'edit-dossier'
    },
    profileNr () {
      return (this.profiles.length > 0) ? this.profiles[this.activeTab].nr : null
    },
    hasBundles () {
      if (this.dossier.id) {
        return this.dossier.bundles.length > 0
      }

      return false
    },
    profiles () {
      if (this.activeSource === 3) {
        return this.source.profielen.filter((profile) => {
          return this.item.refs['per-profiel'].find((x) => {
            return x.profiel === profile.nr
          })
        })
      } else {
        return this.source.profielen.filter((profile) => {
          if (this.activeSource === 1) {
            return this.item.omschrijving.aanvullend.find((x) => {
              return x.referentie === profile.nr
            })
          } else {
            return this.item.gedrag.aanvullend.find((x) => {
              return x.referentie === profile.nr
            })
          }
        })
      }
    },
    hasAanvullend () {
      if (this.activeSource === 3) {
        let additionalItems = this.item.refs['per-profiel'].filter((profiel) => {
          return profiel.refs.length > 0
        })

        return additionalItems.length > 0
      } else {
        return this.item.omschrijving.aanvullend.length > 0
      }
    }
  },
  created () {
    this.isExpanded = this.isOpened
  },
  watch: {
    isOpened () {
      this.isExpanded = this.isOpened
    }
  },
  methods: {
    counter (payload) {
      const count = () => {
        return this.counts.find((x) => {
          return x.itemNr === payload.itemNr &&
            x.kvNr === payload.kvNr &&
            x.profileNr === payload.profileNr &&
            x.categoryNr === payload.categoryNr
        })
      }

      count ? count.count = payload.count : this.counts.push(payload)
    },
    toggle () {
      this.isExpanded = !this.isExpanded
    },
    toggleProfile (index) {
      this.activeTab = index
    },
    getProfile (profileNr) {
      return this.source.profielen.find(x => x.nr === profileNr)
    },
    profileIsSet (profileNr) {
      return this.dossier.bundles.map(bundle => bundle.profile_nr).filter(nr => nr === parseInt(profileNr)).length > 0
    },
    getCategory (categoryNr) {
      return this.source.categorien.find(category => category.nr === categoryNr)
    },
    hasItemsWithoutQuestions () {
      return this.counts.filter(count => count.count === 0).length > 0
    }
  }
}
</script>

<style scoped>
  .card {
    margin-bottom: 30px;
  }

  .is-toggle a {
    border: 0;
    border-radius: 0 !important;
  }

  .kennisenvaardigheden .card-content {
    border-bottom: 1px solid hsl(0, 0%, 86%);
  }

  .counter {
    margin-left: 5px;
    margin-top: -10px;
    height: 0.75rem;
    width: 0.75rem;
    font-size: 0.5rem;
    display: none;
  }

  /* .counter.is-danger {
     animation: blinker 2s linear infinite;
  }

  @keyframes blinker {
    50% {
      opacity: 0;
    }
  } */

  li {
    list-style-type: none;
  }
</style>
+
